import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeroDrawingOverlay } from '@/components/feature/DrawingLines';

const slides = [
  {
    url: 'https://public.readdy.ai/ai/img_res/aafae31599e6dd3e84b1fb8ded19f7b2.jpg',
    label: 'The Studio',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=extreme%20close%20up%20macro%20photography%20fine%20line%20botanical%20tattoo%20delicate%20rose%20and%20thin%20stems%20on%20pale%20skin%20ultra%20detailed%20black%20ink%20linework%20elegant%20minimalist%20tattoo%20art%20soft%20diffused%20studio%20lighting%20shallow%20depth%20of%20field%20luxury%20editorial%20photography%20premium%20tattoo%20craftsmanship%20artistic%20beauty%20dark%20background%20cinematic%20high%20end%20fashion%20magazine%20aesthetic&width=1440&height=900&seq=seonyul_slide_new_02&orientation=landscape',
    label: 'Fine Line Art',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=beautiful%20young%20Korean%20woman%20lying%20down%20on%20white%20treatment%20bed%20receiving%20professional%20eyebrow%20tattoo%20semi%20permanent%20makeup%20procedure%20in%20luxury%20beauty%20clinic%20white%20minimal%20interior%20soft%20warm%20overhead%20lighting%20serene%20relaxed%20expression%20flawless%20glowing%20skin%20premium%20cosmetic%20tattoo%20studio%20editorial%20photography%20elegant%20sophisticated%20atmosphere%20reclined%20position%20top%20view%20angle&width=1440&height=900&seq=seonyul_slide_new_05&orientation=landscape',
    label: 'Eyebrow Art',
  },
];

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
      setTimeout(() => setTextVisible(true), 300);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[700px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className="absolute inset-0"
          style={{ opacity: current === idx ? 1 : 0, transition: 'opacity 1500ms ease-in-out' }}
        >
          <img
            src={slide.url}
            alt={slide.label}
            className="w-full h-full object-cover object-top"
            loading={idx === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {/* Gradient overlays — PC는 밝게, 모바일은 기존 유지 */}
      {/* 모바일 오버레이 */}
      <div
        className="absolute inset-0 z-10 md:hidden"
        style={{
          background: 'linear-gradient(to right, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.45) 100%)',
        }}
      />
      <div
        className="absolute inset-0 z-10 md:hidden"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 60%)',
        }}
      />
      {/* PC 오버레이 — 더 밝게 */}
      <div
        className="absolute inset-0 z-10 hidden md:block"
        style={{
          background: 'linear-gradient(to right, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.38) 55%, rgba(0,0,0,0.18) 100%)',
        }}
      />
      <div
        className="absolute inset-0 z-10 hidden md:block"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.40) 0%, transparent 55%)',
        }}
      />

      {/* Drawing overlay */}
      <HeroDrawingOverlay />



      {/* Content */}
      <div
        ref={heroRef}
        className="relative z-20 h-full flex flex-col justify-center px-5 md:px-16 lg:px-24 w-full"
      >
        {/* Main heading */}
        <div className="max-w-2xl w-full">
          <div
            className="overflow-hidden"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
              transitionDelay: '150ms',
              marginBottom: '0.35rem',
            }}
          >
            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-light text-white"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.08em', lineHeight: '1.1', fontSize: 'clamp(32px, 8vw, 72px)' }}
            >
              Where Lines
            </h1>
          </div>
          <div
            className="overflow-hidden"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
              transitionDelay: '280ms',
              marginBottom: '0.35rem',
            }}
          >
            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-light text-white"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.08em', lineHeight: '1.1', fontSize: 'clamp(32px, 8vw, 72px)' }}
            >
              Become
            </h1>
          </div>
          <div
            className="overflow-hidden"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
              transitionDelay: '420ms',
              marginBottom: '0.5rem',
            }}
          >
            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-light"
              style={{
                fontFamily: "'Cinzel', serif",
                letterSpacing: '0.08em',
                lineHeight: '1.1',
                fontSize: 'clamp(32px, 8vw, 72px)',
                background: 'linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0.45) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Art.
            </h1>
          </div>

          {/* Brand name eyebrow — 모바일에서 영어 헤딩 바로 아래 붙임 */}
          <div
            className="transition-all duration-700 ease-out mb-6 md:hidden"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '480ms',
            }}
          >
            <div className="flex items-center gap-3 mt-1">
              <div className="w-6 h-px bg-white/40" />
              <div className="flex flex-col">
                <span
                  className="text-white/90 text-sm tracking-[0.28em] font-light leading-none"
                  style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif" }}
                >
                  선율
                </span>
                <span
                  className="text-[8px] tracking-[0.38em] uppercase text-white/40 font-light mt-0.5"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  SEONYUL LINE STUDIO
                </span>
              </div>
            </div>
          </div>

          {/* Brand name eyebrow — 데스크탑 원래 위치 유지 */}
          <div
            className="hidden md:flex items-center gap-4 mb-10 -mt-1"
            style={{
              opacity: textVisible ? 1 : 0,
              transition: 'all 0.7s ease-out',
              transitionDelay: '480ms',
            }}
          >
            <div className="w-8 h-px bg-white/40" />
            <div className="flex flex-col">
              <span
                className="text-white/90 text-lg tracking-[0.32em] font-light leading-none"
                style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif" }}
              >
                선율
              </span>
              <span
                className="text-[9px] tracking-[0.42em] uppercase text-white/40 font-light mt-0.5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                SEONYUL LINE STUDIO
              </span>
            </div>
          </div>

          {/* Description */}
          <div
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.8s ease-out',
              transitionDelay: '600ms',
            }}
          >
            <p
              className="text-sm md:text-base text-white/60 mb-10 max-w-sm font-light"
              style={{
                fontFamily: "'Nanum Myeongjo', serif",
                letterSpacing: '0.12em',
                lineHeight: '1.85',
              }}
            >
              선 하나에서 시작되는 예술.<br />
              선율은 타투를 삶의 언어로 만듭니다.
            </p>
          </div>

          {/* Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.8s ease-out',
              transitionDelay: '750ms',
            }}
          >
            <Link
              to="/brand"
              className="inline-flex items-center justify-center px-8 py-3.5 border border-white/70 text-white text-[10px] tracking-[0.22em] uppercase font-medium hover:bg-white hover:text-[#222222] transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm w-full sm:w-auto"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Explore Our Story
            </Link>
            <Link
              to="/portfolio"
              className="inline-flex items-center justify-center px-8 py-3.5 bg-white text-[#222222] text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              View Portfolio
              <i className="ri-arrow-right-line ml-2 text-sm" />
            </Link>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div
        className="absolute bottom-10 left-8 md:left-16 lg:left-24 z-20 flex items-center gap-3"
        style={{
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-out',
          transitionDelay: '1000ms',
        }}
      >
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className="cursor-pointer transition-all duration-500"
          >
            <div
              className={`h-px transition-all duration-500 ${
                current === idx ? 'w-10 bg-white' : 'w-4 bg-white/30'
              }`}
            />
          </button>
        ))}
        <span className="text-white/40 text-xs tracking-[0.2em] ml-2">
          {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-10 right-10 flex flex-col items-center gap-3 z-20"
        style={{
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-out',
          transitionDelay: '1200ms',
        }}
      >
        <div className="w-px h-16 bg-white/30 relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full bg-white/70"
            style={{ animation: 'scrollLine 2s ease-in-out infinite', height: '40%' }}
          />
        </div>
        <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Scroll</span>
      </div>

      <style>{`
        @keyframes fadeInLine {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(300%); }
        }
      `}</style>
    </section>
  );
}
